<template>
  <div class="global-search-container">
    <zm-button
      v-if="isMobileViewport"
      :icon="Magnifier"
      type="secondary"
      class="global-search-header-mobile-button"
      :aria-label="$t('aria.search')"
      @click="showSearchPopover" />

    <zm-button
      v-else
      class="global-search-header-button"
      :aria-expanded="dialogVisible"
      aria-haspopup="dialog"
      aria-controls="global-search-dialog"
      aria-labelledby="global-search-trigger-name global-search-trigger-shortcut"
      aria-keyshortcuts="Meta+K Control+K"
      :icon="Magnifier"
      type="secondary"
      neutral
      @click="showSearchPopover">
      <span id="global-search-trigger-name">{{ $t('search') }}</span>
      <span id="global-search-trigger-shortcut" class="global-search-shortcut">
        {{ shortcutHint }}
      </span>
    </zm-button>

    <zm-dialog
      id="global-search-dialog"
      v-model="dialogVisible"
      :aria-label="$t('aria.search')"
      :teleported="false"
      size=""
      close-on-click-modal
      class="global-search-dialog"
      @close="handleClearQuery">
      <template #header>
        <form class="global-search-form" role="search" @submit.prevent @keydown="onSearchKeydown">
          <zm-input
            ref="searchInputRef"
            v-model="query"
            class="global-search-input"
            size="lg"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="listboxExpanded"
            :aria-controls="activeListboxId"
            :aria-activedescendant="activeOptionId"
            :placeholder="placeholder"
            :aria-label="placeholderAria"
            :lead-icon="Magnifier"
            bare
            @change="clearKeyboardEventState">
            <template #append-select>
              <zm-button
                v-show="query.length"
                type="tertiary"
                class="global-search-clear-btn"
                @click="query = ''">
                {{ $t('clear') }}
              </zm-button>

              <zm-button
                :icon="Close"
                type="tertiary"
                class="global-search-close-btn"
                :aria-label="$t('aria.close')"
                @click="closeSearchPopover" />
            </template>
          </zm-input>
        </form>
      </template>

      <zm-tabs v-if="showUserTab || showSettingsTab" v-model="activeName" class="mgt-md">
        <zm-tab-pane v-if="showUserTab" name="users" :aria-label="tabAccessibleName('users')">
          <template #label>
            <span>{{ $t('users') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.users"
              size="lg"
              :is-blue="activeName === 'users'"
              :is-grey="activeName !== 'users'" />
          </template>
        </zm-tab-pane>

        <zm-tab-pane
          v-if="showSettingsTab"
          name="settings"
          :aria-label="tabAccessibleName('settings')">
          <template #label>
            <span>{{ isUserSearch ? $t('userSettings') : $t('settings') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.settings"
              size="lg"
              :is-blue="activeName === 'settings'"
              :is-grey="activeName !== 'settings'" />
          </template>
        </zm-tab-pane>

        <zm-tab-pane name="navigation" :aria-label="tabAccessibleName('navigation')">
          <template #label>
            <span>{{ $t('navigation') }}</span>
            <zm-badge
              v-if="hasSearched"
              type="counter"
              :count="counters.navigation"
              size="lg"
              :is-blue="activeName === 'navigation'"
              :is-grey="activeName !== 'navigation'" />
          </template>
        </zm-tab-pane>
      </zm-tabs>

      <div class="gs-sr-only" aria-live="polite" aria-atomic="true">{{ resultAnnouncement }}</div>

      <div v-show="hasSearched" class="gs-result-container" @keydown="onListKeydown">
        <users-pane
          v-if="showUserTab"
          ref="usersPaneRef"
          :active="activeName === 'users'"
          listbox-id="gs-listbox-users"
          option-id-prefix="gs-option-users"
          :active-index="activeName === 'users' ? activeIndex : -1"
          @update:active-index="activeIndex = $event" />

        <settings-pane
          v-if="showSettingsTab"
          ref="settingsPaneRef"
          :active="activeName === 'settings'"
          :rule-level="settingsRuleLevel"
          listbox-id="gs-listbox-settings"
          option-id-prefix="gs-option-settings"
          :active-index="activeName === 'settings' ? activeIndex : -1"
          @update:active-index="activeIndex = $event" />

        <navigation-pane
          ref="navigationPaneRef"
          :active="(!showUserTab && !showSettingsTab) || activeName === 'navigation'"
          :search-level="searchLevel"
          listbox-id="gs-listbox-navigation"
          option-id-prefix="gs-option-navigation"
          :active-index="activeName === 'navigation' ? activeIndex : -1"
          @update:active-index="activeIndex = $event" />
      </div>

      <div v-show="!hasSearched" class="gs-guide">
        <img src="/assets/magnifying-glass.png" width="120" height="120" alt="" />
        <p class="gs-guide-tips">{{ $t('searchGuide') }}</p>
      </div>
    </zm-dialog>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { Magnifier, Close } from '@zoom/icons-vue'
import {
  ZmInput,
  ZmDialog,
  ZmButton,
  ZmTabs,
  ZmTabPane,
  ZmBadge,
  hasClass,
  removeClass
} from '@zoom/zoom-ui-vue3'
import { trackEvent } from '@/shared/event-tracker'
import usersPane from './users.vue'
import settingsPane from './settings.vue'
import navigationPane from './navigation.vue'

const { t } = useI18n()

const props = defineProps({
  isMobileViewport: Boolean,
  searchLevel: {
    type: String,
    default: 'admin'
  }
})

const isUserSearch = computed(() => props.searchLevel === 'user')
const settingsRuleLevel = computed(() => (isUserSearch.value ? 'user' : 'account'))

const SEARCH_DEBOUNCE_MS = 300
const AUTO_SWITCH_DELAY_MS = 100
const ADMIN_MENU_LOADED_EVENT = 'admin-side-menu-loaded'

const isSearching = ref(false)

const dialogVisible = ref(false)
const query = ref('')
const hasSearched = ref(false)
const tabOverride = ref('')
const menusData = ref([])
const activeIndex = ref(-1)
const resultAnnouncement = ref('')

const usersPaneRef = ref(null)
const settingsPaneRef = ref(null)
const navigationPaneRef = ref(null)
const searchInputRef = ref(null)

const counters = reactive({
  users: 0,
  settings: 0,
  navigation: 0
})

const searchedTabs = reactive(new Set())

let debounceTimer = 0
let autoSwitchTimer = 0
let searchToken = 0
const idleCancels = new Set()

const syncMenus = () => {
  const menus = window.sideMenu?.menusData
  menusData.value = Array.isArray(menus) ? menus : []
}

const showUserTab = computed(() => {
  if (isUserSearch.value) return false

  return menusData.value.some(
    (menu) =>
      menu.id === 'sidemenu-user-management' &&
      menu.items?.some((item) => item.id === 'user-menu-item-users')
  )
})

const showSettingsTab = computed(() => {
  if (isUserSearch.value) return true

  return menusData.value.some((menu) => menu.id === 'settings-policies')
})

const visibleTabs = computed(() => [
  ...(showUserTab.value ? ['users'] : []),
  ...(showSettingsTab.value ? ['settings'] : []),
  'navigation'
])

const activeName = computed({
  get() {
    const tabs = visibleTabs.value

    if (tabOverride.value && tabs.includes(tabOverride.value)) {
      return tabOverride.value
    }

    return tabs[0] || 'navigation'
  },

  set(name) {
    tabOverride.value = name
  }
})

const placeholderScope = computed(() => {
  if (isUserSearch.value) return 'UserSettings'
  if (showUserTab.value && showSettingsTab.value) return 'All'
  if (showUserTab.value) return 'Users'
  if (showSettingsTab.value) return 'Settings'
  return 'Nav'
})

const placeholder = computed(() => t(`searchPlaceholder${placeholderScope.value}`))

const placeholderAria = computed(() => t(`aria.searchPlaceholder${placeholderScope.value}`))

const activePane = () => tabRefs[activeName.value]?.value

const resultCount = () => activePane()?.getNavigableCount?.() ?? 0

const listboxExpanded = computed(() => hasSearched.value && resultCount() > 0)

const activeListboxId = computed(() =>
  listboxExpanded.value ? `gs-listbox-${activeName.value}` : undefined
)

const activeOptionId = computed(() => {
  if (!listboxExpanded.value || activeIndex.value < 0) return undefined
  return `gs-option-${activeName.value}-${activeIndex.value}`
})

const tabLabel = (name) => {
  if (name === 'users') return t('users')
  if (name === 'settings') return isUserSearch.value ? t('userSettings') : t('settings')
  return t('navigation')
}

const tabAccessibleName = (name) => {
  const label = tabLabel(name)
  if (!hasSearched.value) return label
  return `${label} ${counters[name] ?? 0}`
}

const syncActiveIndex = () => {
  activeIndex.value = -1
}

const announceActiveResults = () => {
  if (!hasSearched.value) {
    resultAnnouncement.value = ''
    return
  }

  const pane = activePane()
  const label = tabLabel(activeName.value)

  if (pane?.isLoading?.()) {
    resultAnnouncement.value = t('searching', [label])
    return
  }

  if (!searchedTabs.has(activeName.value)) {
    resultAnnouncement.value = ''
    return
  }

  const count = resultCount()
  const messageKey = {
    users: 'aria.usersResults',
    settings: 'aria.settingsResults',
    navigation: 'aria.navigationResults'
  }[activeName.value]

  resultAnnouncement.value = count > 0 ? t(messageKey, [count]) : ''
}

const scrollActiveOption = () => {
  const optionId = activeOptionId.value
  if (!optionId) return

  nextTick(() => {
    document.getElementById(optionId)?.scrollIntoView({ block: 'nearest' })
  })
}

const moveActive = (delta) => {
  const count = resultCount()
  if (count <= 0) return

  const current = activeIndex.value
  if (current < 0) {
    if (delta < 0) return
    activeIndex.value = 0
    scrollActiveOption()
    return
  }

  const next = current + delta
  if (next < 0 || next >= count) return

  activeIndex.value = next
  scrollActiveOption()
}

const isQueryInput = (event) => {
  const exposed = searchInputRef.value?.inputRef
  const nativeInput = exposed instanceof HTMLElement ? exposed : exposed?.value
  if (nativeInput) return event.target === nativeInput
  return event.target?.tagName === 'INPUT'
}

const handleResultKey = (event) => {
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    if (resultCount() <= 0) return
    event.preventDefault()
    moveActive(event.key === 'ArrowDown' ? 1 : -1)
    return
  }

  if (event.key === 'Enter' && activeIndex.value >= 0 && resultCount() > 0) {
    event.preventDefault()
    activePane()?.activateAt?.(activeIndex.value)
  }
}

const onSearchKeydown = (event) => {
  if (event.isComposing || event.keyCode === 229) return
  if (!isQueryInput(event)) return
  handleResultKey(event)
}

const onListKeydown = (event) => {
  if (event.isComposing || event.keyCode === 229) return
  if (!event.target?.closest?.('[role="listbox"]')) return
  handleResultKey(event)
}

const shortcutHint =
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || '') ||
  (navigator.userAgent || '').includes('Mac')
    ? '⌘K'
    : 'Ctrl+K'

const resetCounters = () => {
  counters.users = 0
  counters.settings = 0
  counters.navigation = 0
}

const resetSearchResults = () => {
  hasSearched.value = false
  searchedTabs.clear()
  resetCounters()
  activeIndex.value = -1
  resultAnnouncement.value = ''
}

const resetSearchUi = () => {
  tabOverride.value = ''
  resetSearchResults()
}

const showSearchPopover = () => {
  syncMenus()
  dialogVisible.value = true
  trackEvent({
    linkText: 'start search',
    linkUrl: 'undefined',
    uiLocation: `web portal global search ${props.searchLevel === 'user' ? '- user' : ''}`
  })
}

const closeSearchPopover = () => {
  dialogVisible.value = false
}

const handleClearQuery = () => {
  query.value = ''
  cancelSearch()
  resetSearchUi()
}

const isSearchHotkey = (event) =>
  !event.repeat &&
  !event.altKey &&
  !event.shiftKey &&
  (event.metaKey || event.ctrlKey) &&
  event.code === 'KeyK'

const onGlobalKeydown = (event) => {
  if (!isSearchHotkey(event)) return

  event.preventDefault()
  showSearchPopover()
}

const clearKeyboardEventState = () => {
  if (hasClass(document.body, 'is-vue3-keyboard-event')) {
    removeClass(document.body, 'is-vue3-keyboard-event')
  }
}

const scheduleIdle = (callback) => {
  let cancelled = false
  let id

  const run = (...args) => {
    if (cancelled) return

    idleCancels.delete(cancel)
    callback(...args)
  }

  const cancel = () => {
    cancelled = true
    idleCancels.delete(cancel)

    if (typeof window.cancelIdleCallback === 'function' && typeof id === 'number') {
      window.cancelIdleCallback(id)
    } else {
      window.clearTimeout(id)
    }
  }

  if (typeof window.requestIdleCallback === 'function') {
    id = window.requestIdleCallback(run)
  } else {
    id = window.setTimeout(run, 1)
  }

  idleCancels.add(cancel)
}

const cancelIdleWork = () => {
  idleCancels.forEach((cancel) => cancel())
  idleCancels.clear()
}

const cancelDebounce = () => {
  if (!debounceTimer) return

  window.clearTimeout(debounceTimer)
  debounceTimer = 0
}

const cancelAutoSwitch = () => {
  if (!autoSwitchTimer) return

  window.clearTimeout(autoSwitchTimer)
  autoSwitchTimer = 0
}

const invalidateSearch = () => {
  searchToken += 1
  cancelIdleWork()

  Object.values(tabRefs).forEach((paneRef) => {
    paneRef.value?.cancel?.()
  })

  return searchToken
}

const cancelSearch = () => {
  cancelDebounce()
  cancelAutoSwitch()
  invalidateSearch()
}

const tabRefs = {
  users: usersPaneRef,
  settings: settingsPaneRef,
  navigation: navigationPaneRef
}

const searchTab = async (tabName, token) => {
  const pane = tabRefs[tabName]?.value

  if (!pane || token !== searchToken) return

  if (tabName === activeName.value) {
    activeIndex.value = -1
    resultAnnouncement.value = t('searching', [tabLabel(tabName)])
  }

  try {
    counters[tabName] = (await pane.search(query.value)) || 0
  } catch (error) {
    if (token !== searchToken) return

    console.error(`Search ${tabName} failed:`, error)
    counters[tabName] = 0
  } finally {
    if (token === searchToken) {
      searchedTabs.add(tabName)
      if (tabName === activeName.value) {
        syncActiveIndex()
        announceActiveResults()
      }
    }
  }
}

const focusSearchInput = () => {
  nextTick(() => {
    requestAnimationFrame(() => {
      searchInputRef.value?.focus?.()
    })
  })
}

const switchToFirstResultTab = () => {
  if (counters[activeName.value] > 0) return

  const targetTab = visibleTabs.value.find((tab) => counters[tab] > 0)

  if (!targetTab) return

  tabOverride.value = targetTab

  focusSearchInput()
}

const scheduleAutoSwitch = () => {
  cancelAutoSwitch()

  autoSwitchTimer = window.setTimeout(() => {
    autoSwitchTimer = 0

    if (searchToken) {
      switchToFirstResultTab()
    }
  }, AUTO_SWITCH_DELAY_MS)
}

const runSearch = async () => {
  const token = invalidateSearch()
  const tabs = visibleTabs.value

  searchedTabs.clear()
  resetCounters()
  hasSearched.value = true
  isSearching.value = true

  if (!tabOverride.value) {
    tabOverride.value = tabs[0] || 'navigation'
  }

  const firstTab = tabs[0]

  if (firstTab) {
    await searchTab(firstTab, token)
  }

  if (token !== searchToken) return

  await runIdleSearches(tabs.slice(1), token)

  if (token === searchToken) {
    isSearching.value = false
  }
}
const runIdleSearches = async (tabs, token) => {
  for (const tabName of tabs) {
    if (token !== searchToken) return

    await new Promise((resolve) => {
      scheduleIdle(() => {
        searchTab(tabName, token).finally(resolve)
      })
    })
  }

  if (token === searchToken) {
    scheduleAutoSwitch()
  }
}

watch(query, (value) => {
  const keyword = value?.trim() ?? ''

  cancelDebounce()
  cancelAutoSwitch()

  if (!keyword) {
    cancelSearch()
    resetSearchResults()
    focusSearchInput()
    return
  }

  debounceTimer = window.setTimeout(
    () => {
      debounceTimer = 0
      runSearch()
    },
    activeName.value === 'navigation' ? 0 : SEARCH_DEBOUNCE_MS
  )
})

watch(activeName, (tabName) => {
  syncActiveIndex()
  announceActiveResults()

  if (isSearching.value || debounceTimer || !query.value.trim() || searchedTabs.has(tabName)) {
    return
  }

  searchTab(tabName, searchToken)
})

watch(dialogVisible, (visible) => {
  if (!visible) return

  syncMenus()

  focusSearchInput()
})

syncMenus()

onMounted(() => {
  syncMenus()

  window.addEventListener('keydown', onGlobalKeydown, true)
  window.addEventListener(ADMIN_MENU_LOADED_EVENT, syncMenus)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown, true)
  window.removeEventListener(ADMIN_MENU_LOADED_EVENT, syncMenus)

  cancelSearch()
})
</script>

<style lang="scss">
.global-search-container {
  flex: 1;
  text-align: center;
  flex-grow: 100;
  display: flex;
}
.global-search-header-button {
  max-width: 320px;
  flex: 1;
  color: var(--zoom-color-text-strong-neutral, #555b62);
  .global-search-shortcut::before {
    content: ' (';
  }
  .global-search-shortcut::after {
    content: ')';
  }
}
.is-mobile-viewport {
  .global-search-dialog {
    width: 100%;
    margin-left: 0;
    left: 0;
    top: 0;
    border-radius: 0;
    bottom: 0;
  }
}
.global-search-dialog {
  position: absolute;
  left: 50%;
  width: 900px;
  margin-left: -450px;
  top: 16px;
  border-radius: 12px;
  border: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
  .fe-popups-dialog__header {
    padding: 6px 16px;
    line-height: 32px;
    border-bottom: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
    .fe-popups-input__inner.is-leaded {
      padding-left: 22px;
    }
  }
  .gs-guide {
    padding-top: 80px;
    padding-bottom: 80px;
    text-align: center;
    img {
      width: 120px;
      height: 120px;
    }
    .gs-guide-tips {
      margin-top: 8px;
      color: var(--zoom-color-text-text-strong-neutral, #555b62);
    }
  }
  .fe-popups-dialog__body {
    padding: 0;
    &.is-lost-footer {
      margin-bottom: 0;
    }
  }
  .fe-popups-tabs__nav-wrap {
    margin-left: 24px;
    margin-right: 24px;
  }
  .gs-result-container {
    padding: 12px 24px;
    max-height: 480px;
    overflow: auto;
    scrollbar-width: thin;
    scrollbar-color: var(--zoom-color-fill-contrary-transparent) transparent;
    &::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--zoom-color-fill-contrary-transparent);
      border-radius: 999px;
    }
    &::-webkit-scrollbar-track {
      background-color: transparent;
    }
  }

  .gs-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 60px 0;
    flex-direction: column;
    .empty-sub {
      color: var(--zoom-color-text-strong-neutral, #555b62);
      font-size: 14px;
      line-height: 18px;
      margin-top: 16px;
    }
    .empty-main {
      color: var(--zoom-color-text-stronger-neutral, #222325);
      font-size: 16px;
      line-height: 20px;
    }
  }

  .gs-results-list {
    list-style: none;
    padding: 0;
    margin: 0 -8px;

    &[role='listbox']:focus-visible {
      outline: 2px solid var(--zoom-color-border-primary);
      outline-offset: 2px;
    }

    .gs-result-item {
      border-radius: 8px;
      padding: 8px 12px;
      cursor: pointer;
      transition: background-color 0.2s;
      &:hover,
      &:focus-visible,
      &.is-active {
        text-decoration: none;
        background-color: var(--zoom-color-state-subtle-neutral-hover, rgba(110, 118, 128, 0.12));
      }
      // &:focus-visible,
      // &.is-active {
      //   outline: 2px solid var(--zoom-color-border-primary);
      //   outline-offset: 2px;
      // }
    }
  }

  .gs-setting-section,
  .gs-navigation-section {
    text-align: left;

    .gs-setting-name,
    .gs-navigation-name {
      margin: 0 0 4px;
      line-height: 20px;
      font-weight: 600;
      color: var(--zoom-color-text-stronger-neutral, #323539);
    }

    .gs-setting-description,
    .gs-navigation-path {
      margin: 0;
      line-height: 16px;
      font-size: 12px;
      color: var(--zoom-color-text-strong-neutral, #555b62);
    }
  }
  .fe-popups-highlight-text {
    mark {
      padding: 0;
    }
  }
}
.fe-popups-overlay:has(.global-search-dialog) {
  background-color: rgba(255, 255, 255, 0);
}
.gs-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.global-search-form {
  width: 100%;
  min-width: 0;
}
.global-search-input {
  .fe-popups-input__leading {
    left: 0;
  }
  .fe-popups-input__append-select {
    position: relative;
  }
  .global-search-clear-btn {
    color: var(--zoom-color-text-neutral, #6e7680);
    margin-right: 8px;
  }
  .global-search-close-btn {
    box-sizing: content-box;
    margin-left: 8px;
    &:before {
      position: absolute;
      left: -8px;
      top: 0;
      bottom: 0;
      content: '';
      border-left: 1px solid var(--zoom-color-border-subtle-neutral, #dfe3e8);
    }
  }
}
</style>
